import { CalendarDate } from '@internationalized/date';
import {
  CurrencyCode,
  InvoiceType,
  Money,
  PaymentTerm,
  enumCurrencyCode,
  enumInvoiceType,
} from '@rematter/frontend-lib-api-client';
import { atom } from 'jotai';

import { invoiceCreateAtom } from '../_graphql/mutationInvoiceCreate';
import { invoiceGetNextDocumentNumberAtom } from '../_graphql/mutationInvoiceGetNextDocumentNumber';
import { invoiceUpdateAtom } from '../_graphql/mutationInvoiceUpdate';
import { invoiceDetailQueryAtom } from '../_graphql/queryInvoiceDetail';
import { InvoiceFormState, InvoiceLineItemFormState } from '../types';

import { calculateTotalPrice } from '~/modules/trade/utils/money';

const initialInvoiceFormState: InvoiceFormState = {
  id: undefined,
  type: enumInvoiceType.OutboundInvoice,
  documentNumber: undefined,
  documentDate: null,
  dueDate: null,
  accountId: null,
  facilityId: null,
  paymentTerm: null,
  notes: null,
  currencyCode: enumCurrencyCode.USD,
  lineItems: [],
};

// 🔘🔘🔘 PLAIN ATOMS - Form State
export const $invoiceForm = atom<InvoiceFormState>(initialInvoiceFormState);
export const $currentStep = atom<number>(1);
export const $isSubmitting = atom<boolean>(false);
export const $errors = atom<Record<string, string>>({});

// 🟠🟠🟠 ACTIONS - Form Updates
export const $updateInvoiceForm = atom(
  null,
  (get, set, updates: Partial<InvoiceFormState>) => {
    const currentForm = get($invoiceForm);
    set($invoiceForm, { ...currentForm, ...updates });
  },
);

export const $resetInvoiceForm = atom(null, (get, set) => {
  set($invoiceForm, initialInvoiceFormState);
  set($currentStep, 1);
  set($errors, {});
});

export const $setCurrentStep = atom(null, (get, set, step: number) => {
  set($currentStep, step);
});

// 🟠🟠🟠 ACTIONS - Line Item Management
export const $addLineItem = atom(null, (get, set) => {
  const form = get($invoiceForm);
  const maxSortOrder = form.lineItems.reduce(
    (max, item) => Math.max(max, item.sortOrder),
    -1,
  );
  const newItem: InvoiceLineItemFormState = {
    id: `temp-${Date.now()}-${Math.random()}`,
    description: '',
    quantity: null,
    unit: null,
    unitPrice: null,
    totalPrice: null,
    itemId: null,
    groupName: null,
    sortOrder: maxSortOrder + 1,
    isNew: true,
  };

  set($invoiceForm, {
    ...form,
    lineItems: [...form.lineItems, newItem],
  });
});

export const $updateLineItem = atom(
  null,
  (
    get,
    set,
    {
      index,
      updates,
    }: { index: number; updates: Partial<InvoiceLineItemFormState> },
  ) => {
    const form = get($invoiceForm);
    const updatedItems = [...form.lineItems];
    const currentItem = updatedItems[index];

    const updatedItem = { ...currentItem, ...updates };

    if (updates.quantity !== undefined || updates.unitPrice !== undefined) {
      updatedItem.totalPrice = calculateTotalPrice(
        updatedItem.unitPrice,
        updatedItem.quantity,
      );
    }

    updatedItems[index] = updatedItem;

    set($invoiceForm, {
      ...form,
      lineItems: updatedItems,
    });
  },
);

export const $removeLineItem = atom(null, (get, set, index: number) => {
  const form = get($invoiceForm);
  const updatedItems = form.lineItems.filter((_, i) => i !== index);

  set($invoiceForm, {
    ...form,
    lineItems: updatedItems,
  });
});

// 🟠🟠🟠 ACTIONS - Validation
export const INVOICE_REQUIRED_FIELDS = [
  'type',
  'documentDate',
  'facilityId',
  'accountId',
  'paymentTerm',
] as const;

export type InvoiceRequiredField = (typeof INVOICE_REQUIRED_FIELDS)[number];

const fieldValidators: Record<
  InvoiceRequiredField,
  (form: InvoiceFormState) => boolean
> = {
  type: (form) => !form.type,
  documentDate: (form) => !form.documentDate,
  facilityId: (form) => !form.facilityId,
  accountId: (form) => !form.accountId,
  paymentTerm: (form) => !form.paymentTerm,
};

const fieldErrorMessages: Record<InvoiceRequiredField, string> = {
  type: 'Invoice type is required',
  documentDate: 'Document date is required',
  facilityId: 'Facility is required',
  accountId: 'Account is required',
  paymentTerm: 'Payment term is required',
};

const getMissingRequiredFields = (
  form: InvoiceFormState,
): InvoiceRequiredField[] =>
  INVOICE_REQUIRED_FIELDS.filter((field) => fieldValidators[field](form));

export const $validateInvoiceForm = atom(
  null,
  (get, set): { isValid: boolean; errors: Record<string, string> } => {
    const form = get($invoiceForm);
    const missingFields = getMissingRequiredFields(form);

    const errors = missingFields.reduce(
      (acc, field) => ({
        ...acc,
        [field]: fieldErrorMessages[field],
      }),
      {} as Record<string, string>,
    );

    set($errors, errors);
    return { isValid: Object.keys(errors).length === 0, errors };
  },
);

export const $invoiceMissingRequiredFields = atom((get) => {
  const form = get($invoiceForm);
  return getMissingRequiredFields(form);
});

export const $invoiceIsMissingRequired = atom(
  (get) => get($invoiceMissingRequiredFields).length > 0,
);

// 🟢🟢🟢 REMOTE DATA - Existing Invoice
export const $existingInvoice = invoiceDetailQueryAtom((get) => ({
  variables: {
    id: get($invoiceForm).id ?? '',
  },
  skip: !get($invoiceForm).id || get($invoiceForm)?.id?.startsWith('temp-'),
}));

// 🟠🟠🟠 ACTION - Next Document Number
export const $nextInvoiceDocumentNumber = invoiceGetNextDocumentNumberAtom();

// 🟠🟠🟠 ACTION -> CREATE invoice
export const $createInvoice = invoiceCreateAtom();

// 🟠🟠🟠 ACTION -> UPDATE invoice
export const $updateInvoiceAction = invoiceUpdateAtom();

// 🟠🟠🟠 ACTION -> Populate Form from Invoice Record
export const $populateInvoiceFormFromRecord = atom(
  null,
  (
    get,
    set,
    invoice: {
      id: string;
      type: InvoiceType;
      documentNumber: string;
      documentDate: CalendarDate | null;
      dueDate: CalendarDate | null;
      accountId: string | null;
      facilityId: string;
      paymentTerm: PaymentTerm | null;
      notes: string | null;
      currencyCode: CurrencyCode;
      lineItems: {
        id: string;
        description: string;
        quantity: number | null;
        unit: string | null;
        unitPrice: Money | null;
        totalPrice: Money | null;
        itemId: string | null;
        groupName: string | null;
        sortOrder: number;
      }[];
    },
  ) => {
    set($invoiceForm, {
      id: invoice.id,
      type: invoice.type,
      documentNumber: invoice.documentNumber,
      documentDate: invoice.documentDate,
      dueDate: invoice.dueDate,
      accountId: invoice.accountId,
      facilityId: invoice.facilityId,
      paymentTerm: invoice.paymentTerm,
      notes: invoice.notes,
      currencyCode: invoice.currencyCode,
      lineItems: invoice.lineItems.map((li) => ({
        id: li.id,
        description: li.description,
        quantity: li.quantity,
        unit: li.unit,
        unitPrice: li.unitPrice,
        totalPrice: li.totalPrice,
        itemId: li.itemId,
        groupName: li.groupName,
        sortOrder: li.sortOrder,
        isNew: false,
      })),
    });
  },
);
